import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useCallback, useState } from "react";
import DialogCrudPerfil from "./DialogCrudPerfil";
import { TPerfilValue } from "..";
import {
  IDetalheUsuarioPerfil,
} from "../../../../types/api/configuracoes/detalhar-usuario";
import DialogDadosExtras from "./DialogDadosExtras";

export default function SelecaoPerfis({
  perfis,
  onSubmit,
  onDelete,
}: SelecaoPerfisProps) {
  const [openModal, setOpenModal] = useState<TOpenModalPerfil>({
    modalState: false,
    data: null,
  });
  
  const [modalParams, setModalParams] = useState<IModalParametrosState>({
    open: false,
    title: "",
    perfil: null,
  });

  const handleOpenModalPerfil = useCallback(
    (action: "create" | "close" | "edit", value?: TOpenModalPerfil) => {
      if (action === "edit" && value) setOpenModal(value);
      if (action === "close") setOpenModal({ modalState: false, data: null });
      if (action === "create") setOpenModal({ modalState: true, data: null });
    },
    []
  );

  const handleOpenModalParams = useCallback((perfil: IDetalheUsuarioPerfil) => {
    setModalParams({ 
      open: true, 
      title: `Parâmetros (${perfil.nome})`,
      perfil 
    });
  }, []);

  const handleCloseModalParams = useCallback(() => {
    setModalParams(prev => ({ ...prev, open: false }));
  }, []);

  const renderEmptyState = () => (
    <>
      <Typography variant="h6">Vincular Perfis</Typography>
      <Box sx={emptyStateStyles}>
        <Typography fontWeight="bold" mb={1}>
          Nenhum perfil de acesso vinculado ao usuário
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => handleOpenModalPerfil("create")}
        >
          Adicionar
        </Button>
      </Box>
    </>
  );

  const renderPerfisTable = () => (
    <>
      <Box sx={headerStyles}>
        <Typography variant="h6">Perfis Vinculados</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => handleOpenModalPerfil("create")}
        >
          Adicionar
        </Button>
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="tabela de perfis">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 200 }}>
                <b>Nome</b>
              </TableCell>
              <TableCell align="center" sx={{ minWidth: 300, maxWidth: 400 }}>
                <b>Parâmetros</b>
              </TableCell>
              <TableCell align="right" sx={{ width: 100 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {perfis.map((perfil, index) => {
              const editProps = {
                modalState: true,
                data: { ...perfil, index },
              };

              return (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {perfil.nome}
                  </TableCell>
                  <TableCell>
                    {renderTableParams(perfil)}
                  </TableCell>
                  <TableCell align="right" sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      aria-label="deletar"
                      onClick={() => onDelete(editProps.data)}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      aria-label="editar"
                      onClick={() => handleOpenModalPerfil("edit", editProps)}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const renderTableParams = useCallback((perfil: IDetalheUsuarioPerfil) => {
    const hasParams = hasAnyParameter(perfil);

    if (!hasParams) {
      return (
        <Box display="flex" justifyContent="center">
          <Typography color="textSecondary">-</Typography>
        </Box>
      );
    }

    return (
      <Box display="flex" justifyContent="center">
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="contained"
            onClick={() => handleOpenModalParams(perfil)}
          >
            Ver Parâmetros
          </Button>
        </Box>
      </Box>
    );
  }, [handleOpenModalParams]);

  const hasAnyParameter = (perfil: IDetalheUsuarioPerfil): boolean => {
    return !!(
      perfil.loja ||
      perfil.vendedor ||
      perfil.condicoes_pagamento_blocked?.length ||
      perfil.limite_alcada ||
      perfil.tipo_publico?.length
    );
  };

  return (
    <>
      {perfis.length === 0 ? renderEmptyState() : renderPerfisTable()}

      {openModal.modalState && (
        <DialogCrudPerfil
          onSubmit={onSubmit}
          values={openModal.data}
          valueIndex={openModal.data?.index}
          open={openModal.modalState}
          handleClose={() => handleOpenModalPerfil("close")}
        />
      )}

      <DialogDadosExtras
        open={modalParams.open}
        title={modalParams.title}
        perfil={modalParams.perfil}
        onClose={handleCloseModalParams}
      />
    </>
  );
}

const emptyStateStyles = {
  mt: 1,
  width: "100%",
  height: 200,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  border: "1px solid #005540",
  borderRadius: 1,
};

const headerStyles = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

// Tipos
interface SelecaoPerfisProps {
  perfis: IDetalheUsuarioPerfil[];
  onSubmit: (valor: TPerfilValue) => void;
  onDelete: (valor: TOpenModalPerfilData) => void;
}

export type TOpenModalPerfil = {
  modalState: boolean;
  data: TOpenModalPerfilData;
};

export type TOpenModalPerfilData = (IDetalheUsuarioPerfil & { index: number }) | null;


export interface IModalParametrosState {
  open: boolean;
  title: string;
  perfil: IDetalheUsuarioPerfil | null;
}